import {
  AvailableVersions,
  Language,
  ReportType,
} from "@/domain/records/types";
import {
  SetupConfig,
  type SetupConfigUpdatableField,
} from "@/domain/records/value-objects/SetupConfig";
import { ValidationRules } from "@/domain/records/value-objects/ValidationRules";

interface SetupDataSnapshot {
  notes?: string | null;
  goals?: string | null;
  guide?: string | null;
  selected_report_types?: string[] | null;
  report_language?: Language | string | null;
  participant_count?: number | null;
  respondent_known_names?: string[] | null;
  is_with_transcript?: boolean | null;
  is_with_insight_report?: boolean | null;
  custom_prompt?: string | null;
  stt_provider?: string | null;
}

/**
 * Domain Service for Setup Configuration business logic
 * Clean Architecture - Domain Layer
 */
export class SetupDomainService {
  /**
   * Create default setup configuration
   */
  createDefaultConfig(
    recordId: string,
    isTextFile: boolean = false
  ): SetupConfig {
    return new SetupConfig(
      recordId,
      "", // goals
      "", // notes
      "", // guide
      isTextFile ? ["General"] : ["Q&A", "General", "JTBD"], // reportTypes
      Language.SameAsTranscript, // language
      undefined, // participantCount
      [""], // respondentNames
      !isTextFile, // isWithTranscript (false for text files)
      true, // isWithInsightReport (true for text files)
      undefined, // customPrompt
      "auto"
    );
  }

  /**
   * Initialize setup config from existing data
   */
  initializeFromData(
    recordId: string,
    data: SetupDataSnapshot | null,
    availableVersions: AvailableVersions | null | undefined
  ): SetupConfig {
    // Determine selected report types based on available versions
    let selectedReportTypes: ReportType[] = [];

    if (availableVersions) {
      // If reports exist, show what was actually generated
      const hasQa = (availableVersions.qa_report || []).length > 0;
      const hasGeneral = (availableVersions.general || []).length > 0;
      const hasJtbd = (availableVersions.jtbd_markdown_report || []).length > 0;
      const hasPersona = (availableVersions.persona_report || []).length > 0;
      const hasCustom = (availableVersions.custom_report || []).length > 0;

      if (hasQa) selectedReportTypes.push("Q&A");
      if (hasGeneral) selectedReportTypes.push("General");
      if (hasJtbd) selectedReportTypes.push("JTBD");
      if (hasPersona) selectedReportTypes.push("Persona");
      if (hasCustom) selectedReportTypes.push("Custom");
    } else {
      // Default selection if no reports exist yet
      selectedReportTypes = ["Q&A", "General", "JTBD"];
    }

    if (
      selectedReportTypes.length === 0 &&
      Array.isArray(data?.selected_report_types)
    ) {
      selectedReportTypes = this.normalizeReportTypes(
        data?.selected_report_types
      );
    }

    return new SetupConfig(
      recordId,
      data?.goals || "", // goals (research context)
      data?.notes || "", // notes (additional info)
      data?.guide || "", // guide (interview guide)
      selectedReportTypes,
      this.parseLanguage(data?.report_language),
      data?.participant_count ?? undefined,
      data?.respondent_known_names && data.respondent_known_names.length > 0
        ? data.respondent_known_names
        : [""],
      data?.is_with_transcript ?? true,
      data?.is_with_insight_report ?? true,
      data?.custom_prompt ?? undefined,
      this.parseSttProvider(data?.stt_provider)
    );
  }

  /**
   * Validate setup configuration
   */
  validateConfig(config: SetupConfig) {
    return ValidationRules.validateSetupConfig(config);
  }

  /**
   * Toggle report type selection
   */
  toggleReportType(config: SetupConfig, reportType: ReportType): SetupConfig {
    const exists = config.reportTypes.includes(reportType);

    let newReportTypes: ReportType[];
    if (exists) {
      newReportTypes = config.reportTypes.filter((type) => type !== reportType);
      // Ensure at least one report type remains
      if (newReportTypes.length === 0) {
        newReportTypes = config.reportTypes; // Keep the current one
      }
    } else {
      newReportTypes = [...config.reportTypes, reportType];
    }

    return config.with({ reportTypes: newReportTypes });
  }

  /**
   * Update form field
   */
  updateField<TField extends SetupConfigUpdatableField>(
    config: SetupConfig,
    field: TField,
    value: SetupConfig[TField]
  ): SetupConfig {
    return config.with({ [field]: value });
  }

  /**
   * Add respondent name
   */
  addRespondent(config: SetupConfig): SetupConfig {
    const maxRespondents = config.participantCount || 6;
    if (config.respondentNames.length >= Math.min(maxRespondents, 3)) {
      return config; // Max reached
    }

    return config.with({
      respondentNames: [...config.respondentNames, ""],
    });
  }

  /**
   * Remove respondent name
   */
  removeRespondent(config: SetupConfig, index: number): SetupConfig {
    if (config.respondentNames.length <= 1) {
      return config; // Keep at least one
    }

    const newNames = config.respondentNames.filter((_, i) => i !== index);
    return config.with({ respondentNames: newNames });
  }

  /**
   * Update respondent name
   */
  updateRespondent(
    config: SetupConfig,
    index: number,
    name: string
  ): SetupConfig {
    const newNames = [...config.respondentNames];
    newNames[index] = name;
    return config.with({ respondentNames: newNames });
  }

  /**
   * Check if form can be submitted
   */
  canSubmit(config: SetupConfig): boolean {
    const validation = this.validateConfig(config);
    return validation.isValid;
  }

  /**
   * Get report type display names
   */
  getReportTypeDisplayNames(): Record<ReportType, string> {
    return {
      "Q&A": "Q&A",
      General: "General",
      JTBD: "JTBD",
      Persona: "Persona",
      Custom: "Custom",
    };
  }

  /**
   * Get available languages
   */
  getAvailableLanguages(): Language[] {
    return Object.values(Language);
  }

  private parseLanguage(language: unknown): Language {
    if (language && typeof language === "string") {
      const normalized = language.trim().toLowerCase();
      const match = (Object.values(Language) as string[]).find(
        (option) => option.toLowerCase() === normalized
      );
      if (match) {
        return match as Language;
      }
    } else if (
      language &&
      Object.values(Language).includes(language as Language)
    ) {
      return language as Language;
    }

    return Language.SameAsTranscript;
  }

  private normalizeReportTypes(types: string[]): ReportType[] {
    const validTypes: ReportType[] = [
      "Q&A",
      "General",
      "JTBD",
      "Persona",
      "Custom",
    ];

    const normalized = types
      .map((type) => type.trim())
      .filter((type): type is ReportType =>
        (validTypes as string[]).includes(type)
      );

    return Array.from(new Set(normalized));
  }

  private parseSttProvider(
    value: unknown
  ): "auto" | "assembly" | "soniox" {
    if (typeof value !== "string") {
      return "auto";
    }
    const normalized = value.trim().toLowerCase();
    if (normalized === "assembly") {
      return "assembly";
    }
    if (normalized === "soniox") {
      return "soniox";
    }
    return "auto";
  }
}
